import type { ReactElement } from 'react';
import { PublicationStatus, type PublicationDto } from '@gr/core/distribution/publishing/dto';
import { Link, useT } from '../../../../app/di';
import { formatTime } from '../../../../shared/format';
import { CalendarEntryText, CalendarGrid, CalendarTone, type CalendarEntry } from '../CalendarGrid/CalendarGrid';
import { conflictKey, dayKey, type CalendarDay, type CalendarView } from '../model/calendar';

export interface PublicationCalendarProps {
  blogId: string;
  timezone: string;
  view: CalendarView;
  days: readonly CalendarDay[];
  today: CalendarDay;
  /** Месяц на экране: дни соседних месяцев приглушены. */
  month: number;
  byDay: ReadonlyMap<string, readonly PublicationDto[]>;
  conflicts: ReadonlySet<string>;
  /** Сколько публикаций видно в клетке месяца, остальное — «ещё N» (ui.calendar). */
  monthCellLimit?: number;
}

const TONES: Record<PublicationStatus, CalendarTone> = {
  [PublicationStatus.Planned]: CalendarTone.Accent,
  [PublicationStatus.Due]: CalendarTone.Warning,
  [PublicationStatus.Published]: CalendarTone.Success,
  [PublicationStatus.Cancelled]: CalendarTone.Cancelled,
};

/** SCH-UC-7, SCH-RULE-4: публикации по дням; конфликты отмечены. */
export function PublicationCalendar({ blogId, timezone, byDay, conflicts, ...grid }: PublicationCalendarProps): ReactElement {
  const { locale } = useT();
  const entriesOf = (day: CalendarDay): CalendarEntry[] =>
    (byDay.get(dayKey(day)) ?? []).map((publication) => {
      const open = publication.status === PublicationStatus.Due || publication.status === PublicationStatus.Planned;
      return {
        key: publication.id,
        tone: TONES[publication.status],
        flagged: conflicts.has(conflictKey(publication)),
        content: (
          <Link to={open ? 'publication' : 'post'} params={open ? { blogId, publicationId: publication.id } : { blogId, postId: publication.postId }}>
            <CalendarEntryText time={formatTime(publication.plannedAt ?? '', locale, timezone)} meta={publication.channelName} title={publication.postTitle} />
          </Link>
        ),
      };
    });
  return <CalendarGrid {...grid} entriesOf={entriesOf} />;
}
